import React from 'react';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import './Legal.css';

const MessagingPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <SEO 
        title="Promotional Messaging Policy - TableChat"
        description="TableChat Promotional Messaging Policy. Learn about marketing messages you may receive after opting in through the TableChat platform at a participating restaurant."
        canonical="https://tablechat.io/messaging-policy"
      />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Promotional Messaging Policy', url: 'https://tablechat.io/messaging-policy' },
      ])} />
      <div className="container">
        <div className="legal-header">
          <h1>Promotional Messaging Policy</h1>
          <p className="last-updated">Last updated: March 18, 2026</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Scope of This Policy</h2>
            <p>
              This Promotional Messaging Policy governs marketing and promotional communications you may receive
              after voluntarily opting in through the TableChat platform at a participating restaurant
              ("Restaurant") or through the TableChat website (tablechat.io).
            </p>
            <p>This policy applies to promotional messages sent:</p>
            <ul>
              <li>By TableChat directly, regarding its own products, features, and services</li>
              <li>By TableChat, acting on behalf of a Restaurant</li>
              <li>By the Restaurant directly</li>
              <li>
                By third-party marketing platforms used by the Restaurant (such as Klaviyo, Mailchimp, Constant
                Contact, or similar services)
              </li>
            </ul>
            <p>This policy applies to SMS/text messages and email communications.</p>
            <p>
              For information about how TableChat collects and uses data more broadly, please see our{' '}
              <a href="/privacy">Privacy Policy</a>.
            </p>
            <p>
              Consent to receive promotional messages is not required to purchase goods or services, dine at a
              restaurant, or use the TableChat platform.
            </p>
          </section>

          <section>
            <h2>2. Consent to Receive Messages</h2>
            <h3>Restaurant Opt-In</h3>
            <p>
              By voluntarily providing a phone number or email address through the TableChat platform at a
              participating restaurant, you agree that the restaurant you are visiting may send you messages related
              to your visit, restaurant promotions, loyalty programs, or other restaurant communications.
            </p>
            <p>
              These messages may be delivered directly by the restaurant, through the TableChat platform on the
              restaurant's behalf, or through a third-party marketing platform used by the restaurant.
            </p>
            <h3>Website Opt-In</h3>
            <p>
              By voluntarily providing your phone number through the TableChat website (tablechat.io) and checking
              the consent box, you agree to receive promotional and marketing messages from TableChat directly,
              including product updates, feature announcements, and offers related to the TableChat platform.
            </p>
            <p>
              Website opt-in is independent of any restaurant opt-in. Your contact information collected through
              the website is used by TableChat for its own marketing purposes and is not shared with restaurants.
            </p>
            <h3>General Terms</h3>
            <p>
              Message frequency may vary. Standard message and data rates may apply. You may opt out of receiving
              messages at any time by replying STOP to a text message, clicking the unsubscribe link in an email, or
              by following other instructions provided in the message.
            </p>
            <p>
              Consent to receive promotional messages is not a condition of purchase or use of the TableChat platform.
            </p>
          </section>

          <section>
            <h2>3. Who May Send You Messages</h2>
            <p>By opting in, you agree that you may receive promotional messages from one or more of the following:</p>
            <h3>TableChat Directly</h3>
            <p>
              If you opt in through the TableChat website, TableChat may send you messages about its own products,
              features, services, and offers. These messages are sent by TableChat as the sender of record and are
              not associated with any specific restaurant.
            </p>
            <h3>The Restaurant</h3>
            <p>
              If you opt in at a participating restaurant, the Restaurant where you opted in may send you
              promotional communications, including offers, loyalty rewards, and event announcements.
            </p>
            <h3>TableChat, Acting on Behalf of the Restaurant</h3>
            <p>
              TableChat may send messages on behalf of the Restaurant to deliver promotions, rewards, confirmations,
              or related communications.
            </p>
            <h3>Third-Party Marketing Platforms Used by the Restaurant</h3>
            <p>
              The Restaurant may use third-party marketing service providers (such as Klaviyo, Mailchimp, Constant
              Contact, or similar platforms) to send promotional messages on its behalf.
            </p>
            <p>
              Messages will clearly identify the sender — either TableChat or the Restaurant — and may indicate
              that the message was delivered via TableChat.
            </p>
            <p>
              TableChat acts as a service provider and platform to facilitate communications on behalf of
              Restaurants. Restaurants are the sender of record for promotional messages they initiate and are
              responsible for their content and frequency.
            </p>
          </section>

          <section>
            <h2>4. Information Collected and Consent Records</h2>
            <h3>Restaurant Opt-In</h3>
            <p>
              When you opt in at a participating restaurant, TableChat and the Restaurant may collect and
              maintain the following information:
            </p>
            <ul>
              <li>Your mobile phone number and/or email address</li>
              <li>The Restaurant where consent was provided</li>
              <li>Table and session context at the time of opt-in</li>
              <li>Consent timestamp and policy version</li>
              <li>Verification status via one-time passcode</li>
              <li>Technical metadata such as IP address and device information for compliance and audit purposes</li>
            </ul>
            <p>
              This information may be shared with the Restaurant and the Restaurant's authorized marketing service
              providers solely for the purpose of delivering promotional communications.
            </p>
            <h3>Website Opt-In</h3>
            <p>
              When you opt in through the TableChat website, TableChat collects and maintains the following
              information:
            </p>
            <ul>
              <li>Your mobile phone number</li>
              <li>Consent timestamp and policy version</li>
              <li>Technical metadata such as IP address and device information for compliance and audit purposes</li>
            </ul>
            <p>
              Contact information collected through the website is retained by TableChat and is not shared with
              restaurants or third-party marketing platforms.
            </p>
            <h3>General</h3>
            <p>
              TableChat maintains records of consent for compliance with applicable laws and carrier requirements.
            </p>
          </section>

          <section>
            <h2>5. Types of Messages You May Receive</h2>
            <h3>From a Restaurant</h3>
            <p>If you opt in at a restaurant, you may receive promotional messages such as:</p>
            <ul>
              <li>Promotional offers and discount codes</li>
              <li>Loyalty rewards and VIP offers</li>
              <li>Event announcements and invitations</li>
              <li>Seasonal or limited-time specials</li>
              <li>Restaurant news and marketing communications</li>
            </ul>
            <p>The Restaurant determines the content of its promotional messages.</p>
            <p>TableChat does not control or review Restaurant-initiated marketing content.</p>
            <h3>From TableChat (Website Opt-In)</h3>
            <p>If you opt in through the TableChat website, you may receive messages such as:</p>
            <ul>
              <li>Product updates and new feature announcements</li>
              <li>Promotional offers related to the TableChat platform</li>
              <li>Industry news and tips for restaurant operators</li>
              <li>Service updates and enhancements</li>
            </ul>
            <p>TableChat determines the content and frequency of its own direct marketing messages.</p>
          </section>

          <section>
            <h2>6. Message Frequency</h2>
            <p>Message frequency varies.</p>
            <p>
              For restaurant opt-ins, you may receive promotional messages occasionally or periodically, depending
              on the Restaurant's marketing activity. TableChat-initiated messages on behalf of the Restaurant are
              typically limited to confirmations, reward delivery, or occasional promotional communications.
            </p>
            <p>
              For website opt-ins, TableChat may send messages periodically regarding product updates, offers, and
              announcements. Message frequency will not exceed a reasonable volume.
            </p>
          </section>

          <section>
            <h2>7. Automated Technology Disclosure</h2>
            <p>
              By opting in, you expressly consent to receive promotional and marketing messages at the phone number
              and/or email address you provide.
            </p>
            <p>You understand and agree that these messages may be sent using:</p>
            <ul>
              <li>Automated telephone dialing systems</li>
              <li>Automated messaging technology</li>
              <li>Or other electronic messaging systems</li>
            </ul>
            <p>Consent is not a condition of purchase or use of the TableChat platform.</p>
          </section>

          <section>
            <h2>8. How to Opt Out</h2>
            <p>You may opt out of promotional communications at any time.</p>
            <h3>For SMS/Text Messages</h3>
            <ul>
              <li>Reply <strong>STOP</strong> to unsubscribe</li>
              <li>Reply <strong>HELP</strong> for assistance</li>
            </ul>
            <p>
              You will receive a confirmation message after opting out. No further promotional text messages will be
              sent unless you opt in again.
            </p>
            <h3>For Email Messages</h3>
            <ul>
              <li>Click the "Unsubscribe" link included in any promotional email</li>
            </ul>
            <h3>Direct Request</h3>
            <ul>
              <li>Email <a href="mailto:support@tablechat.io">support@tablechat.io</a></li>
              <li>Or contact the Restaurant directly</li>
            </ul>
            <p>Opt-out requests will be honored promptly in accordance with applicable law.</p>
            <p>
              Opting out of promotional messages does not affect your ability to use TableChat's in-restaurant
              features.
            </p>
          </section>

          <section>
            <h2>9. Message and Data Rates</h2>
            <p>
              Standard message and data rates may apply based on your mobile carrier and service plan.
            </p>
            <p>TableChat and the Restaurant are not responsible for carrier charges.</p>
            <p>Mobile carriers are not liable for delayed or undelivered messages.</p>
          </section>

          <section>
            <h2>10. Data Sharing and Service Providers</h2>
            <h3>Restaurant Opt-In</h3>
            <p>Contact information collected through a restaurant opt-in may be shared with:</p>
            <ul>
              <li>The Restaurant where you opted in</li>
              <li>Marketing service providers used by that Restaurant</li>
              <li>Messaging delivery providers such as SMS gateway providers</li>
            </ul>
            <p>
              This information is shared solely to deliver promotional communications on behalf of the Restaurant.
            </p>
            <h3>Website Opt-In</h3>
            <p>
              Contact information collected through the TableChat website is retained by TableChat and may be
              shared with messaging delivery providers solely for the purpose of delivering TableChat's own
              marketing communications. Website opt-in data is not shared with restaurants or their marketing
              service providers.
            </p>
            <h3>General</h3>
            <p>
              TableChat does not sell, rent, or share your personal information with unaffiliated third parties for
              their independent marketing purposes.
            </p>
          </section>

          <section>
            <h2>11. Consent Scope</h2>
            <h3>Restaurant Opt-Ins</h3>
            <p>
              Your consent at a restaurant applies only to the specific Restaurant where you opted in.
              If you opt in at multiple Restaurants, each consent applies independently to that Restaurant.
              Restaurants do not receive your contact information from unrelated Restaurants.
            </p>
            <h3>Website Opt-In</h3>
            <p>
              Your consent through the TableChat website applies to communications from TableChat directly and is
              independent of any restaurant opt-in. Opting in or out on the website does not affect your opt-in
              status at any restaurant, and vice versa.
            </p>
          </section>

          <section>
            <h2>12. Data Retention and Deletion</h2>
            <p>
              Your contact information will be retained while you remain opted in to receive promotional
              communications.
            </p>
            <p>
              If you opt out, your contact information will be suppressed from future promotional messaging.
            </p>
            <p>
              You may request deletion of your marketing data by contacting{' '}
              <a href="mailto:support@tablechat.io">support@tablechat.io</a>.
            </p>
            <p>
              Certain records may be retained as required for legal compliance and audit purposes.
            </p>
          </section>

          <section>
            <h2>13. Applicable Laws and Compliance</h2>
            <p>
              This promotional messaging program is administered in compliance with applicable laws, including:
            </p>
            <ul>
              <li>Telephone Consumer Protection Act (TCPA)</li>
              <li>CAN-SPAM Act</li>
              <li>California Consumer Privacy Act (CCPA/CPRA), where applicable</li>
              <li>Mobile carrier messaging requirements and industry standards</li>
            </ul>
            <p>
              Restaurants are independently responsible for their compliance when sending promotional messages.
            </p>
          </section>

          <section>
            <h2>14. Geographic Scope</h2>
            <p>
              This promotional messaging program is intended for users located in the United States.
            </p>
            <p>Additional rights may apply depending on your state or jurisdiction.</p>
          </section>

          <section>
            <h2>15. Contact Information</h2>
            <p>
              If you have questions about this policy, or wish to exercise your rights, contact:
            </p>
            <div className="contact-info">
              <p><strong>TableChat Support</strong></p>
              <p><a href="mailto:support@tablechat.io">support@tablechat.io</a></p>
              <p><a href="https://tablechat.io" target="_blank" rel="noopener noreferrer">https://tablechat.io</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MessagingPolicy;
